/*
 * IMPORTS
 */
import React from 'react'
import _ from 'underscore'
import { useQuery } from '@apollo/client'
import { DataGrid } from '@mui/x-data-grid'
import {
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue
} from '@chakra-ui/react'
import { IoMdAdd } from 'react-icons/io'


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import MccMncSelector from 'components/MccMncForLcr'
import TableSpinner from 'components/TableSpinner'
import { MemoizedSelect } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import LcrReadQuery from './__query__/index.rateLcr.read.query'
import { TbRuler3, TbRulerMeasure } from 'react-icons/tb'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = ['READ_SUCCESSFUL']

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [mcc, setMcc] = React.useState('UN_KNOWN')
  const [mnc, setMnc] = React.useState(['UN_KNOWN'])
  const [trunkType, setTrunkType] = React.useState('DIRECT')
  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({
    'base': true,
    'md': false,
    'lg': false,
    'xl': false
  })

  const _QueryLcrRead = useQuery(LcrReadQuery, {
    'variables': {
      mcc,
      mnc,
      trunkType,
      'skip': paginationModel.page * paginationModel.pageSize,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  // Data assignment.
  if (!_QueryLcrRead.loading && 0 < _QueryLcrRead.data?.RateLcrRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryLcrRead?.data?.RateLcrRead?.length || (0 < _QueryLcrRead?.data?.RateLcrRead?.length && !_.every(_.pluck(_QueryLcrRead?.data?.RateLcrRead, 'status'), j => _successFlags.includes(j)))) _isFirstLoadCompleted.current = false

  // Columns definition
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center'
    },
    {
      'field': 'dialingCode',
      'headerName': 'Dialing Code',
      'description': 'Country dialing code.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 130
    },
    {
      'field': 'mcc',
      'headerName': 'Mcc',
      'description': 'Mobile country code.',
      'editable': false,
      'sortable': true,
      'filterable': TbRulerMeasure,
      'align': 'center',
      'headerAlign': 'center',
      'width': 100
    },
    {
      'field': 'mnc',
      'headerName': 'Mnc',
      'description': 'Mobile network code.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 100
    },
    {
      'field': 'countryName',
      'headerName': 'Country Name',
      'description': 'Name of country.',
      'editable': false,
      'sortable': true,
      'filterable': TbRuler3,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'operator',
      'headerName': 'Operator',
      'description': 'Network operator.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'baseCurrencyRate',
      'headerName': 'Base Currency Rate',
      'description': 'Base rate in currency.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 170
    },
    {
      'field': 'actualRate',
      'headerName': 'Actual Rate',
      'description': 'Actual rate.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 130
    },
    {
      'field': 'currency',
      'headerName': 'Currency',
      'description': 'Rate currency.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 100
    },
    {
      'field': 'accountName',
      'headerName': 'Account Name',
      'description': 'Rate account name.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 180
    },
    {
      'field': 'rateType',
      'headerName': 'Rate Type',
      'description': 'Type of rate.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 130
    }
  ], [])

  // Rows data
  const rows = React.useMemo(() => _.compact(_QueryLcrRead?.data?.RateLcrRead?.map((item, index) =>
    'READ_SUCCESSFUL' === item.status ? ({
      'id': item?.id || `lcr-${index}-${Date.now()}`,
      'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
      'dialingCode': item?.Mcc?.dialingCode || '-',
      'mcc': item?.Mcc?.mcc || '-',
      'mnc': item?.Mnc?.mnc || '-',
      'countryName': item?.Mcc?.countryName || '-',
      'operator': item?.Mnc?.network || '-',
      'baseCurrencyRate': item?.rate || '-',
      'actualRate': item?.rate || '-',
      'currency': item?.Rate?.currency || '-',
      'accountName': item?.Rate?.displayName || '-',
      'rateType': item?.Rate?.type || '-'
    }) : null
  )), [_QueryLcrRead?.data?.RateLcrRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='ratePlan base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          position='relative'
          gap={_isCurrentViewMobile ? '12px' : '14px'}
          bg='white'
          minH={_isCurrentViewMobile ? '128vh' : '98vh'}
          maxH={_isCurrentViewMobile ? '128vh' : '98vh'}
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>

          {/* FILTER SECTION */}
          <Flex
            gap='12px'
            flexWrap='wrap'
            bg='white'
            p='16px'
            borderRadius='12px'
            border='1px solid #C5CFE8'
            alignItems='center'
            justify='space-between'
          >
            <Flex gap='12px' flexWrap='wrap' alignItems='center'>
              <Box>
                <MccMncSelector onChange={__mcc => {
                  setMcc(__mcc?.mcc)
                  setMnc(__mcc?.mnc)
                }} />
              </Box>

              <Box minW='150px' w='200px'>
                <MemoizedSelect
                  label='Type'
                  placeholder='Select Type'
                  className='filter searchInput'
                  w='100%'
                  options={[
                    'DIRECT',
                    'HQ',
                    'SIM',
                    'SPAM',
                    'WHOLESALE',
                    'FULLY_FEATURE',
                    'SS7'
                  ]}
                  onChange={i => setTrunkType(i.target.value)}
                />
              </Box>
            </Flex>

            <Box>
              <DownloadToExcel
                cellsData={_QueryLcrRead.data?.RateLcrRead?.map((item, index) =>
                  'READ_SUCCESSFUL' === item.status ? {
                    'S.No': `${index + (paginationModel.pageSize * paginationModel.page) + 1}.`,
                    'Dialing Code': item?.Mcc?.dialingCode,
                    'Mcc': item?.Mcc?.mcc,
                    'Mnc': item?.Mnc?.mnc,
                    'Country Name': item?.Mcc?.countryName,
                    'Operator': item?.Mnc?.network,
                    'Base Currency Rate': item?.rate,
                    'Actual Rate': item?.rate,
                    'Currency': item?.Rate?.currency,
                    'Account Name': item?.Rate?.displayName,
                    'Rate Type': item?.Rate?.type
                  } : {})}
                headersData={[
                  'S.No.',
                  'Dialing Code',
                  'Mcc',
                  'Mnc',
                  'Country Name',
                  'Operator',
                  'Base Currency Rate',
                  'Actual Rate',
                  'Currency',
                  'Account Name',
                  'Rate Type'
                ].map(i => ({ 'key': i, 'label': i }))}
              />
            </Box>
          </Flex>

          {/* MUI DataGrid */}
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryLcrRead?.data?.RateLcrRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} title='Kindly select Mcc and Mnc.' chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} title='Kindly select Mcc and Mnc.' chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryLcrRead?.loading}

          />
        </Flex>
      </Flex>
    </>
  )
}

Index.propTypes = {}


/*
 * REDUX
 */
export default Index
